import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosClient from '../../api/axiosClient';

export default function CheckoutModal({ plan, onClose }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    schoolName: '',
    studentsCount: 100,
    paymentMode: 'full' // 'full' or 'partial'
  });

  useEffect(() => {
    if (localStorage.getItem('trialToken')) {
      axiosClient.get('/trials/me')
        .then(res => {
          if (res.data?.profile) {
            setFormData(prev => ({
              ...prev,
              name: res.data.profile.name || prev.name,
              email: res.data.profile.email || prev.email,
              schoolName: res.data.profile.school_name || prev.schoolName
            }));
          }
        })
        .catch(err => console.error("Could not fetch user profile for autofill:", err));
    }
  }, []);

  if (!plan) return null;

  const numericPrice = parseInt(plan.price.toString().replace(/\D/g, '') || '0');
  const fullTotal = numericPrice * formData.studentsCount;
  
  let finalPayable = fullTotal;
  if (formData.paymentMode === 'partial') {
    finalPayable = 1000;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosClient.post('/plans/checkout', {
        name: formData.name,
        email: formData.email,
        schoolName: formData.schoolName,
        planName: plan.name,
        studentsCount: formData.studentsCount,
        paymentMode: formData.paymentMode,
        amountPaid: finalPayable
      });
      setSuccess(true);
    } catch (err) {
      console.error("Failed to checkout", err);
      toast.error("Failed to process your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] sm:max-h-[85vh] overflow-hidden flex flex-col relative"
          onClick={(e) => e.stopPropagation()}
        >
          {success ? (
            <div className="p-10 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">Payment Successful!</h3>
              <p className="text-slate-600 mb-6">
                Thank you for subscribing to the <strong>{plan.name}</strong> plan. A receipt has been sent to your email.
              </p>
              <button 
                onClick={onClose} 
                className="w-full bg-primary-600 text-white font-medium py-3 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Complete Purchase</h3>
                  <p className="text-sm text-slate-500 mt-1">Subscribe to {plan.name}</p>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-2 bg-white rounded-full shadow-sm">
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto">
                <div className="flex justify-between items-center bg-primary-50 p-4 rounded-xl border border-primary-100 mb-2">
                  <span className="font-medium text-primary-900">Plan Amount</span>
                  <span className="text-xl font-bold text-primary-700">{plan.price} {plan.period && <span className="text-sm font-normal">{plan.period}</span>}</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                    <input 
                      required 
                      type="text" 
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none transition-shadow" 
                      value={formData.name} 
                      onChange={e => setFormData({...formData, name: e.target.value})} 
                      placeholder="John Doe" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                    <input 
                      required 
                      type="email" 
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none transition-shadow" 
                      value={formData.email} 
                      onChange={e => setFormData({...formData, email: e.target.value})} 
                      placeholder="john@example.com" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">School / Institution Name</label>
                    <input 
                      required 
                      type="text" 
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none transition-shadow" 
                      value={formData.schoolName} 
                      onChange={e => setFormData({...formData, schoolName: e.target.value})} 
                      placeholder="e.g. Springfield Elementary" 
                    />
                  </div>

                  <div className="pt-2">
                    <div className="flex justify-between mb-2">
                      <label className="block text-sm font-medium text-slate-700">Number of Students</label>
                      <span className="font-bold text-primary-600">{formData.studentsCount}</span>
                    </div>
                    <input 
                      type="range" 
                      min="100" 
                      max="2500" 
                      step="100"
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                      value={formData.studentsCount}
                      onChange={e => setFormData({...formData, studentsCount: parseInt(e.target.value)})}
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>100</span>
                      <span>2500+</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <label className="block text-sm font-medium text-slate-700 mb-3">Payment Options</label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${formData.paymentMode === 'full' ? 'border-primary-500 bg-primary-50/50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input 
                          type="radio" 
                          name="paymentMode" 
                          value="full" 
                          className="text-primary-600 focus:ring-primary-500"
                          checked={formData.paymentMode === 'full'}
                          onChange={() => setFormData({...formData, paymentMode: 'full'})}
                        />
                        <span className="text-sm font-medium text-slate-700">Pay Now</span>
                      </label>
                      <label className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${formData.paymentMode === 'partial' ? 'border-primary-500 bg-primary-50/50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input 
                          type="radio" 
                          name="paymentMode" 
                          value="partial" 
                          className="text-primary-600 focus:ring-primary-500"
                          checked={formData.paymentMode === 'partial'}
                          onChange={() => setFormData({...formData, paymentMode: 'partial'})}
                        />
                        <span className="text-sm font-medium text-slate-700">Subscribe & Pay Later</span>
                      </label>
                    </div>
                  </div>

                  {formData.paymentMode === 'partial' && (
                    <div className="animate-in fade-in slide-in-from-top-2 duration-300 bg-amber-50 p-4 rounded-xl border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium">Minimum Rs. 1000 required to activate subscription</p>
                    </div>
                  )}

                  <div className="flex justify-between items-center bg-slate-900 text-white p-4 rounded-xl mt-4">
                    <span className="font-medium">Total Payable Amount</span>
                    <span className="text-xl font-bold">₹{finalPayable.toLocaleString()}</span>
                  </div>
                </div>

                <button 
                  disabled={loading} 
                  type="submit" 
                  className="w-full bg-slate-900 text-white font-medium py-3.5 rounded-xl hover:bg-slate-800 transition-colors mt-6 disabled:opacity-70 flex justify-center items-center gap-2 shadow-lg shadow-slate-900/20"
                >
                  {loading ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Processing...</span>
                  ) : (
                    <span className="flex items-center gap-2">Proceed to Pay ₹{finalPayable.toLocaleString()}</span>
                  )}
                </button>

                <div className="flex items-center justify-center gap-1.5 mt-4 text-xs text-slate-500 font-medium">
                  <ShieldCheck size={14} className="text-emerald-500" />
                  Payments are secure and encrypted
                </div>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
